import { VideoSchema } from "@jmc/core/src/components/VideoSchema/index";
import { WarningMessage } from "@jmc/solid-design-system/src/components/atoms/WarningMessage/WarningMessage";
import { BrightcovePreload } from "@jmc/solid-design-system/src/components/organisms/VideoPlayer/Brightcove/BrightcovePreload";
import {
    getLocalThumbnailUrl,
    SupportedLocalThumbProviders,
    VideoPlayer as SolidVideoPlayer,
} from "@jmc/solid-design-system/src/components/organisms/VideoPlayer/VideoPlayer";
import { ApplicationState } from "@redux/modules";
import { set as setVideoPlayer } from "@redux/modules/videoPlayer";
import classnames from "classnames";
import React, { MutableRefObject } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import yn from "yn";

import {
    JWM_VIDEO_ACCOUNT_ID,
    JWM_VIDEO_APP_ID,
    JWM_VIDEO_PLAYER_ID,
    JWM_VIDEO_PLAYER_INTERACTIVE_ID,
} from "./constants";
import style from "./style.module.scss";

export interface PropTypes {
    videoId: string;
    playerId?: string;
    offset?: number;
    thumbnail?: string;
    onPlay?: () => void;
    showControls?: boolean;
    showLargePlayButton?: boolean;
    contentAlignment?: "left" | "center" | "right";
    contentVerticalAlignment?: "top" | "center" | "bottom";
    hasOverlay?: boolean;
    autoplay?: boolean;
    showInHeader?: boolean;
    seoSummary?: string;
    seoTitle?: string;
    alt?: string;
    mediasource: string;
    interactive_video?: boolean;
    updated_at?: Date;
}

/**
 * If there's is no thumbnail/poster provided, it will return a local URL for the thumbnail image.
 * These are fetched at build time and stored in the public/video-posters directory.
 *
 * @see /scripts/get-video-posters.ts
 * @returns Local URL for the thumbnail image.
 */
export const getLocalThumbnail = (videoId: string, thumbnail: string, provider: string): string => {
    if (
        yn(process.env.GATSBY_DOWNLOAD_VIDEO_POSTERS) &&
        SupportedLocalThumbProviders.includes(provider?.toLowerCase())
    ) {
        return thumbnail ? thumbnail : getLocalThumbnailUrl(videoId, provider);
    } else {
        return thumbnail;
    }
};

export const VideoPlayer = (props: PropTypes): JSX.Element => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const videoPlayerRef = useSelector((state) => (state as ApplicationState).videoPlayer?.videoref);

    const warningMessage = process.env.GATSBY_DISABLE_COOKIEBANNER === "false" &&
        process.env.GATSBY_ONETRUST_DATA_DOMAIN && (
            <WarningMessage
                warning={t(
                    "common:It seems your browser is blocking functional cookies which are required to display videos. To resolve this issue, please update your settings to allow these cookies.",
                )}
                cookieMessage={t("Open cookie settings", { ns: "common" })}
                variant="card"
            />
        );

    let embedUrl = "";
    if (props?.mediasource === "brightcove") {
        embedUrl = `https://players.brightcove.net/${JWM_VIDEO_ACCOUNT_ID}/${
            props?.interactive_video ? JWM_VIDEO_PLAYER_INTERACTIVE_ID : JWM_VIDEO_PLAYER_ID
        }_default/index.html?${props?.videoId}`;
    }
    if (props?.mediasource === "youtube") {
        embedUrl = `https://www.youtube.com/watch?v=${props?.videoId}`;
    }

    const videoSchema = (
        <VideoSchema
            title={props?.seoTitle || ""}
            summary={props?.seoSummary || ""}
            embedUrl={embedUrl}
            uploadDate={props?.updated_at || ""}
            thumbnailUrl={getLocalThumbnail(props.videoId, props.thumbnail, props.mediasource) || ""}
        />
    );

    switch (props?.mediasource) {
        case "youtube":
            return (
                <div className={classnames(style.videoPlayer)}>
                    <SolidVideoPlayer.Youtube
                        {...props}
                        thumbnail={getLocalThumbnail(props.videoId, props.thumbnail, props.mediasource)}
                        warningMessage={warningMessage}
                        onPlay={(videoref: MutableRefObject<unknown>) => {
                            dispatch(setVideoPlayer({ videoref: videoref })); // Set video reference in redux
                        }}
                        playVideoRef={videoPlayerRef}
                        seoTitle={props?.seoTitle}
                        alt={props?.alt}
                    />
                    {videoSchema}
                </div>
            );
        case "brightcove":
            return (
                <div className={classnames(style.videoPlayer)}>
                    <BrightcovePreload
                        // TODO: Rebranding: Remove playerId after rebranding as both JWM and JMC will use the same player
                        playerId={
                            props.playerId
                                ? props.playerId
                                : props?.interactive_video
                                  ? JWM_VIDEO_PLAYER_INTERACTIVE_ID
                                  : JWM_VIDEO_PLAYER_ID
                        }
                        applicationId={JWM_VIDEO_APP_ID}
                        {...props}
                        thumbnail={getLocalThumbnail(props.videoId, props.thumbnail, props.mediasource)}
                        warningMessage={warningMessage}
                        onPlay={(videoref: MutableRefObject<unknown>) => {
                            dispatch(setVideoPlayer({ videoref: videoref, playing: true })); // Set video reference in redux
                        }}
                        onPause={(videoref: MutableRefObject<unknown>) => {
                            dispatch(setVideoPlayer({ videoref: videoref, playing: false })); // Set video reference in redux
                        }}
                        playVideoRef={videoPlayerRef}
                        seoTitle={props?.seoTitle}
                        alt={props?.alt}
                        considerFluidStyle
                        iosFullWindow={props?.interactive_video}
                    />
                    {videoSchema}
                </div>
            );
        default:
            return <p>Unknown media source used in VideoPlayer</p>;
    }
};

export default VideoPlayer;
